/**
 * Fetches Region Geo Modal copy from Contentful (welcome heading + body).
 *
 * @override Re-exports `useRegionGeoModalById` and `useRegionGeoModals` from the
 * parent `pwa-contentful` module so the public API surface remains intact.
 * Without these re-exports, downstream consumers (e.g. `metadata.js`) that rely
 * on the alias resolver get `undefined` and webpack emits an
 * "export ... was not found" warning.
 */
import {print} from 'graphql';

import {useQuery} from '@tanstack/react-query';

import {useService} from '@amblique/base/app/hooks/use-service';
import {ContentfulService} from '@amblique/contentful/app/services/contentful-service';
import {getRegionGeoModalCopy} from '@amblique/king-living/app/contentful/queries/region-geo-modal.queries';

export {
  useRegionGeoModalById,
  useRegionGeoModals,
} from '@amblique/contentful/app/contentful/hooks/use-region-geo-modal';

/**
 * @typedef {{ welcomeHeading: string|null, welcomeBody: string|null }} RegionGeoModalCopy
 */

/**
 * @param {unknown} value
 * @returns {string|null}
 */
function trimNonEmptyString(value) {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

/**
 * @param {Object} [options={}]
 * @param {Object} [options.variables={}] - Extra GraphQL variables (e.g. preview)
 * @param {import('@tanstack/react-query').UseQueryOptions<RegionGeoModalCopy, Error>} [options.queryOptions={}]
 * @param {RequestInit} [options.requestOptions={}]
 */
export function useRegionGeoModalCopy(options = {}) {
  const contentfulService = useService(ContentfulService);
  const {variables = {}, queryOptions = {}, requestOptions = {}} = options;

  return useQuery({
    queryKey: [
      contentfulService?.key,
      'regionGeoModal',
      'copy',
      contentfulService?.locale,
      JSON.stringify(variables)
    ],

    queryFn: async () => {
      if (!contentfulService) {
        throw new Error('ContentfulService not available');
      }

      const data = await contentfulService.query(
        print(getRegionGeoModalCopy),
        {
          locale: contentfulService.locale,
          preview: false,
          ...variables,
        },
        requestOptions
      );

      const item = data?.regionGeoModalCollection?.items?.[0];

      if (!item) {
        return {welcomeHeading: null, welcomeBody: null};
      }

      return {
        welcomeHeading: trimNonEmptyString(item.welcomeHeading),
        welcomeBody: trimNonEmptyString(item.welcomeBody),
      };
    },

    enabled: Boolean(contentfulService),
    staleTime: 5 * 60 * 1000,
    ...queryOptions,
  });
}
